import type { Suggestion } from './api'

/**
 * The judgements the inbox makes about a finding, kept out of the rendering.
 *
 * They are here rather than in the component because they are decisions, not
 * markup: what counts as evidence, and whether a disclosure has anything behind
 * it. Both are easy to get subtly wrong and both are worth asserting without a
 * browser.
 */

/**
 * Is this a fact, or is it just old?
 *
 * The distinction the whole product rests on: age is suspicion, and only
 * evidence makes a page outdated. A writer who cannot tell the two apart will
 * treat every item the same way — which in practice means treating the real
 * ones as casually as the routine ones.
 *
 * `evidence` wins when entries disagree. One proven problem is not softened by
 * two vague ones.
 */
export function basisOf(item: Suggestion): 'evidence' | 'suspicion' | null {
  const weights = item.evidence.map((e) => e.weight).filter(Boolean)
  if (weights.includes('evidence')) return 'evidence'
  if (weights.includes('suspicion')) return 'suspicion'
  return null
}

/**
 * Does the evidence say anything the detail did not?
 *
 * Some analysers produce a single entry whose only content is the sentence
 * already shown above it. Offering "Why this is here" and then repeating that
 * line teaches people the disclosure is empty, and they stop opening it on the
 * findings where it matters — an API diff with a matched path and a line number.
 *
 * The weight is deliberately NOT counted as something added, because it is
 * shown on the row itself. If it were counted, every freshness finding would
 * carry a disclosure whose entire content was a word already visible above it.
 */
export function addsSomething(item: Suggestion): boolean {
  return item.evidence.some(
    (e) =>
      Boolean(e.matched) ||
      typeof e.line === 'number' ||
      (Boolean(e.detail) && e.detail !== item.detail),
  )
}
